/* ==========================================================
   39 DEGREES - The cart drawer, shared chrome on every page.
   PORT onto the Dawn-based theme (Sept 2026). Source: assets/drawer.js
   on the 39D New Theme; logic unchanged, three seams retargeted:

   DOUBLE-ADD GUARD (the important one). Dawn's assets/product-form.js
   already listens for submit on every <product-form> form, calls
   preventDefault() and POSTs /cart/add.js itself. If this script also
   posted on the same submit the customer would get two of everything.
   Chosen path: DAWN DOES THE ADD, this drawer only acknowledges and
   refreshes. Mechanics: (1) the submit handler below runs at document
   level, after Dawn's form-level handler; when the event arrives
   already defaultPrevented the submit has been claimed by another
   script, so we open the drawer with the pouring mark and do NOT
   fetch. Forms nobody claimed (no <product-form>, plain /cart/add
   forms) still take the original path, one fetch, ours. (2) Dawn's
   product-form does `document.querySelector('cart-drawer')` and, when
   it finds nothing, NAVIGATES to /cart after the add. The section
   therefore wraps the drawer in a plain <cart-drawer data-dj-host>
   element (cart_type stays "page", so Dawn's own CartDrawer class is
   never defined and never upgrades it) and this script gives it the
   three methods product-form calls: getSectionsToRender (none: this
   theme has no cart-icon-bubble section, the header is updated by
   hand), setActiveElement, and renderContents, which is our
   "add finished" callback: refresh from /cart.js and render. (3) Dawn
   publishes PUB_SUB_EVENTS.cartUpdate / cartError through pubsub.js;
   we subscribe when those globals exist, so a sold-out add closes the
   drawer (Dawn shows its inline error) and any other add source still
   refreshes us. Off the cart page only: on /cart Dawn's cart.js owns
   the page and runs the production cart-goal reconciler itself.

   HEADER HOOKS: the header cart is <a id="cart-icon-bubble"
   class="header__icon header__icon--cart"> with a .cart-count-bubble
   inside (sections/header.liquid). Clicking it opens the drawer off
   the cart page; the href stays as the no-JS fallback and /cart is
   still reachable by URL. The bubble is rewritten after every change.

   RELOAD ON /cart: gone (6 Oct 2026). Dawn's cart page is server-rendered
   and its cart.js re-renders via the Section Rendering API from ITS writes
   only; if our reconciler moved a gift line the page is stale. It used to
   reload; now rerenderCartPage() redraws <cart-items> in place from the same
   Section Rendering API. Only successful writes count (see cartWrite).
   ==========================================================

   Original notes follow.
   Wired to the real AJAX cart: /cart/add.js, /cart/change.js,
   /cart.js. Any add-to-cart form submission opens the drawer
   in place; the form's normal POST to /cart/add remains the
   no-JS fallback (it navigates to the full /cart page).

   Merchandising note: thresholds, gifts and the conditional
   offer are content, edited on the Cart drawer section (the
   Overlay group in the theme editor), never here. The ladder,
   the legend, every "Add $X for ..." line and each earned /
   next / locked state are computed from that config plus the
   live subtotal. Gift tiers with a gift product configured are
   FUNCTIONAL: the $0 gift line is added and removed by the
   reconciler below (ported from the production store's cart-goal
   mechanism). Gift lines carry properties._cart_goal so they can
   never be confused with paid lines; only the highest earned
   tier is held at any time; tier maths excludes gift cards and
   subscription (selling plan) lines, matching production.

   Rules added 2 Sept 2026 (Suzy):
   1. A gift that has sold out makes its tier VANISH, so the
      customer falls back to the tier below rather than to
      nothing. Availability arrives from snippets/gift-stock.liquid.
   2. A failed gift add is survivable. It used to be counted as a
      successful write, which made the cart page reload, which
      tried again, which reloaded: a sold-out gift was an endless
      reload. Writes are now only counted when they succeed, and a
      variant that fails once is not tried again on this page.
   3. Checkout waits. Both checkout controls now finish any cart
      work in flight and reconcile once more before leaving, so
      nobody reaches the payment screen holding a gift the cart no
      longer earns.
   4. A gift product is a gift line however it got there. Suzy
      broke the first version in ten seconds by adding the $0 mask
      straight from its product page: it carried no _cart_goal tag,
      so the reconciler treated it as something the customer had
      chosen and left five free masks in an empty cart. Now any
      line whose variant is one of the configured gifts is ours to
      manage: removed if unearned, replaced by a properly tagged
      line if earned, quantity pinned to one either way.
   ========================================================== */
(function () {
  'use strict';

  var cfgEl = document.querySelector('[data-drawer-config]');
  var drawer = document.querySelector('[data-dj-drawer]');
  var overlay = document.querySelector('[data-dj-overlay]');
  if (!cfgEl || !drawer || !overlay) return;

  var CFG = JSON.parse(cfgEl.textContent);
  var TIERS = CFG.tiers || [];
  var OFFERS = CFG.offers || [];
  var BEST_SELLERS = CFG.bestSellers || [];
  var RECOS = CFG.recos || [];
  var COPY = CFG.copy || {};
  var SHORT = CFG.shortNames || {};
  var cart = null;
  var lastFocus = null;
  var leaving = false;

  /* Every configured gift variant, taken BEFORE sold-out tiers are
     dropped below: a sold-out gift someone adds by hand must still be
     recognised as a gift and removed, not waved through as a purchase. */
  var GIFT_IDS = (CFG.tiers || []).map(function (t) { return t.giftVariantId; }).filter(Boolean);

  /* Tiers whose gift cannot be honoured are removed before anything
     reads TIERS, so they vanish from the ladder, the spotlight, the
     rows and the reconciler at once. A tier with no gift product (the
     shipping tier) is never affected. */
  (function dropSoldOutTiers() {
    var el = document.querySelector('[data-gift-stock]');
    if (!el) return;
    var gone;
    try { gone = (JSON.parse(el.textContent) || {}).unavailable || []; } catch (e) { return; }
    if (!gone.length) return;
    TIERS = TIERS.filter(function (t) {
      return !t.giftVariantId || gone.indexOf(t.giftVariantId) === -1;
    });
  })();

  function money(cents) { return '$' + (cents / 100).toFixed(2); }
  function paidCount(c) { return c.items.reduce(function (n, it) { return isGiftLine(it) ? n : n + it.quantity; }, 0); }
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function thumb(src, w) {
    if (!src) return '';
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  /* ---------- AJAX cart ---------- */
  /* Instant feedback for any in-drawer mutation: the existing busy
     fade, applied at click time rather than never (the remove path
     previously gave no signal until the round trips finished). render()
     lifts it. pointer-events are cut while busy so a slow write cannot
     be double-fired. */
  function setBusy() {
    var body = drawer.querySelector('[data-dj-body]');
    if (body) body.classList.add('dj-busy');
  }

  /* refresh(fresh): pass a cart object already returned by a mutation
     (POST /cart/change.js responds with the full cart) to skip the
     redundant GET /cart.js round trip. */
  function refresh(fresh) {
    var writesBefore = reconcileWrites;
    var got = fresh
      ? Promise.resolve(fresh)
      : fetch(CFG.cartJsUrl, { credentials: 'same-origin' }).then(function (r) { return r.json(); });
    return got
      .then(function (c) {
        // Paint before the gift reconciler runs: its write+read round
        // trips must never hold up the drawer. The ladder maths is
        // subtotal-driven so it renders correctly either way; a granted
        // gift line simply appears on the re-render below.
        cart = c; syncDeclined(c); render(); updateHeaderCounts(); // 14 Sept 2026: gift opt-out
        return reconcileGifts();
      })
      .then(function () {
        if (reconcileWrites > writesBefore) {
          render(); updateHeaderCounts();
          // The full cart page is server-rendered: if the reconciler just
          // changed gift lines, its HTML is stale. One reload resyncs it.
          // Only SUCCESSFUL writes are counted (see cartWrite), so a gift
          // that cannot be added no longer reloads the page forever.
          // Never reload while the customer is on their way to checkout.
          if (!leaving && location.pathname === CFG.cartUrl) return rerenderCartPage();
        }
      });
  }

  /* 6 Oct 2026: the cart page no longer reloads after a gift swap. Dawn's
     cart page is server-rendered, so after the reconciler adds or removes a
     gift line its <cart-items> markup is stale. Until today that was fixed
     with location.reload(), which is why quantity changes on /cart were kept
     away from the reconciler (a reload under the customer's finger). Now the
     stale part is re-rendered in place from the Section Rendering API, the
     same request assets/cart.js makes in its own onCartUpdate(), so the gift
     line swaps on the page without a reload and quantity changes can run the
     reconciler like every other cart change. Best effort: if the fetch fails
     the page is left as it is and settles on the next load or at checkout. */
  function rerenderCartPage() {
    var host = document.querySelector('cart-items');
    if (!host) return Promise.resolve();
    return fetch(CFG.cartUrl + '?section_id=main-cart-items', { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('cart-items');
        if (!fresh) return;
        host.innerHTML = fresh.innerHTML;
        host.className = fresh.className;
        var footer = document.getElementById('main-cart-footer');
        if (footer) footer.classList.toggle('is-empty', !cart || !cart.items.length);
      })
      .catch(function () {});
  }

  /* ---------- gift reconciler ----------
     The subtotal that earns gifts excludes the gift lines
     themselves, gift cards, and subscription lines (production
     rule). One pass per refresh: remove unearned gift lines, pin
     earned-gift quantity to 1, add the granted tier's gift if
     missing (14 Sept 2026: gift step-down - the granted tier is the
     highest earned one whose gift has not been declined). Each write
     refetches /cart.js so `cart` is always the post-write truth;
     `reconciling` stops recursion. */
  var reconciling = false;
  var reconcileWrites = 0;
  var inFlight = Promise.resolve();
  var failedGifts = {};

  // 14 Sept 2026: gift opt-out. Declined tiers live in the cart attribute
  // _dj_declined (comma-separated gift variant ids; the underscore hides it
  // at checkout) for the life of the cart. `declined` mirrors it in memory
  // and is re-read from every /cart.js response, so the attribute wins.
  var declined = {};
  function syncDeclined(c) {
    declined = {};
    var raw = c && c.attributes && c.attributes._dj_declined;
    if (!raw) return;
    String(raw).split(',').forEach(function (k) { k = k.trim(); if (k) declined[k] = true; });
  }
  function isDeclined(variantId) { return !!declined[String(variantId)]; }
  /* 14 Sept 2026: gift step-down. Every earned gift tier the customer has said
     no to, lowest first. One "left out" line is drawn per entry, and while any
     exist the drawer must not claim every gift is in the box. Sold-out tiers
     are already gone from TIERS, so they can never appear here. */
  function declinedEarnedTiers(sub) {
    return TIERS.filter(function (t) {
      return t.giftVariantId && sub >= t.threshold && isDeclined(t.giftVariantId);
    });
  }
  function setDeclined(variantId, on) {
    var key = String(variantId);
    if (on) declined[key] = true; else delete declined[key];
    setBusy();
    var updateUrl = CFG.updateUrl || (CFG.cartUrl + '/update.js');
    // Let any reconcile pass finish first, so a gift it is adding right now
    // is seen and removed by the refresh below rather than left behind.
    var p = inFlight
      .then(function () { return cartWrite(updateUrl, { attributes: { _dj_declined: Object.keys(declined).join(',') } }); })
      .then(function () { return refresh(cart); }, function () { return refresh(); });
    // Checkout waits on inFlight: cover the window before the attribute lands.
    inFlight = p.then(function () {}, function () {});
    return p;
  }

  /* hasTag: a line the reconciler itself added. isGiftLine: any line
     that IS a gift product, tagged or not. The reconciler manages the
     second set and only trusts the first. */
  function hasTag(item) {
    return !!(item.properties && item.properties._cart_goal === 'true');
  }
  function isGiftLine(item) {
    return hasTag(item) || GIFT_IDS.indexOf(item.variant_id) !== -1;
  }
  function giftSubtotal(c) {
    return c.items.reduce(function (sum, item) {
      if (isGiftLine(item)) return sum;
      if (item.gift_card) return sum;
      if (item.selling_plan_allocation) return sum;
      return sum + item.final_line_price;
    }, 0);
  }
  /* The ladder's earning rule, ignoring opt-outs. 14 Sept 2026: gift step-down
     moved the reconciler and the gift rows onto activeGiftTier() below; this
     stays as the plain "what did the subtotal earn" answer. */
  function earnedGiftTier(sub) {
    var winner = null;
    TIERS.forEach(function (t, idx) {
      if (t.giftVariantId && sub >= t.threshold) winner = idx;
    });
    return winner; // index of the highest earned tier that has a real gift product
  }

  /* 14 Sept 2026: gift step-down. The tier actually granted: the highest earned
     gift tier whose gift has not been declined. Declining the top one now falls
     through to the next earned tier down rather than giving nothing, and keeps
     walking down until an undeclined earned tier is found (null if none is
     left, which is the old no-gift outcome). Sold-out / turned-off tiers were
     dropped from TIERS at start-up (and by dropTier) so they are skipped here
     exactly as before. The free-shipping tier has no giftVariantId and is never
     considered. */
  function activeGiftTier(sub) {
    var winner = null;
    TIERS.forEach(function (t, idx) {
      if (t.giftVariantId && sub >= t.threshold && !isDeclined(t.giftVariantId)) winner = idx;
    });
    return winner;
  }

  /* A gift that would not add is a gift we cannot promise. Drop its tier
     the same way a sold-out one is dropped at start-up, rebuild the
     ladder's spacing, and force a remount so the stations redraw. */
  function dropTier(variantId) {
    var before = TIERS.length;
    TIERS = TIERS.filter(function (t) { return t.giftVariantId !== variantId; });
    if (TIERS.length === before) return;
    computeStations();
    var mount = drawer.querySelector('[data-dj-ladder-mount]');
    if (mount) mount.innerHTML = '';
    // 14 Sept 2026: cart page ladder. The cart page holds a second mount of the
    // same ladder, so it has to be torn down here too or it keeps drawing a
    // station for a tier that has just been taken away.
    var cartMount = document.querySelector('[data-dj-cart-ladder-mount]');
    if (cartMount) cartMount.innerHTML = '';
  }

  /* Only a write that Shopify accepted counts. The old version counted
     the attempt, which is what turned a sold-out gift into a reload
     loop on the cart page. */
  function cartWrite(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error('cart write refused');
      reconcileWrites++;
      return fetch(CFG.cartJsUrl, { credentials: 'same-origin' });
    }).then(function (r) { return r.json(); }).then(function (c) { cart = c; });
  }

  function reconcileGifts() {
    // Already running: hand back the pass in flight rather than a
    // resolved promise, so anything waiting on us (checkout) really waits.
    if (reconciling) return inFlight;
    if (!cart) return Promise.resolve();
    if (!GIFT_IDS.length) return Promise.resolve();
    reconciling = true;

    var sub = giftSubtotal(cart);
    // 14 Sept 2026: gift step-down. Was earnedGiftTier() with the declined tier
    // then nulled out (no gift at all). Now activeGiftTier() steps down past
    // every declined earned tier, so the best undeclined earned gift is held.
    // Lines for declined tiers still fail the `item.variant_id !== wantId` test
    // below and are removed, exactly as before.
    var winIdx = activeGiftTier(sub);
    var wantId = winIdx === null ? null : TIERS[winIdx].giftVariantId;

    // Sequential, not parallel: /cart/change.js line numbers shift
    // after every mutation, so each step recomputes from fresh state.
    var step = Promise.resolve();

    cart.items.forEach(function (item) {
      if (!isGiftLine(item)) return;
      // Gone: any gift that is not the one earned, and any copy of the
      // earned one that we did not add ourselves (no tag). The untagged
      // copy is replaced below by a properly tagged line, so the cart
      // page can tell it apart from a purchase.
      if (item.variant_id !== wantId || !hasTag(item)) {
        step = step.then(function () {
          var line = cart.items.findIndex(function (l) { return l.key === item.key; }) + 1;
          if (line === 0) return; // already gone
          return cartWrite(CFG.changeUrl, { id: item.key, quantity: 0 });
        });
      } else if (item.quantity !== 1) {
        step = step.then(function () {
          return cartWrite(CFG.changeUrl, { id: item.key, quantity: 1 });
        });
      }
    });

    if (wantId) {
      step = step.then(function () {
        var held = cart.items.some(function (l) { return hasTag(l) && l.variant_id === wantId; });
        if (held) return;
        if (failedGifts[wantId]) return; // tried once on this page, refused
        return cartWrite(CFG.addUrl, {
          id: wantId, quantity: 1,
          properties: { _cart_goal: 'true', _cart_goal_tier: String(winIdx + 1) }
        }).catch(function () {
          // Almost always sold out between the page rendering and this
          // moment. Remember it, take the tier away so nothing is
          // promised that cannot be given, and carry on: the customer
          // keeps the tier below.
          failedGifts[wantId] = true;
          dropTier(wantId);
        });
      });
    }

    // Best effort throughout. A failed write must never leave the drawer
    // stuck or throw past the caller.
    inFlight = step.then(
      function () { reconciling = false; },
      function () { reconciling = false; }
    );
    return inFlight;
  }
  function change(line, qty) {
    setBusy();
    return fetch(CFG.changeUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: line, quantity: qty })
    })
      // /cart/change.js answers with the full cart: hand it straight to
      // refresh() and skip a GET round trip. refresh() still reconciles
      // gifts (a quantity drop can lose a tier).
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) { return refresh(c); });
  }
  function addId(id) {
    setBusy();
    return fetch(CFG.addUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: id, quantity: 1 })
    }).then(function (r) {
      if (!r.ok) throw new Error('add failed');
      // /cart/add.js answers with the added line, not the cart, so this
      // path still needs the GET.
      return refresh();
    });
  }

  /* ---------- leaving for checkout ----------
     Every cart change is a request that takes a moment; the checkout
     controls are a plain link and a plain submit, which leave at once.
     So they can carry a gift the cart stopped earning a second ago.
     settleThen finishes what is in flight, reconciles once more against
     the true cart, and only then goes. The timeout is the safety net: a
     slow or dead network must never trap someone in the cart. */
  function settleThen(go) {
    leaving = true;
    var went = false;
    function once() { if (!went) { went = true; go(); } }
    var timer = setTimeout(once, 2500);
    inFlight
      .then(function () { return refresh(); })
      .then(function () { return refresh(); })
      .then(function () { clearTimeout(timer); once(); },
            function () { clearTimeout(timer); once(); });
  }

  /* Dawn port: the header cart is <a id="cart-icon-bubble" class="header__icon
     header__icon--cart"> in sections/header.liquid, rendered inline (this
     theme has no cart-icon-bubble section to re-render), so the bubble is
     updated by hand. The figure is cart.item_count, the same number Liquid
     prints on the next page load, so the bubble never disagrees with itself. */
  function updateHeaderCounts() {
    if (!cart) return;
    var count = cart.item_count;
    var links = document.querySelectorAll('#cart-icon-bubble, .header__icon--cart');
    links.forEach(function (a) {
      var bubble = a.querySelector('.cart-count-bubble');
      if (count <= 0) { if (bubble) bubble.remove(); return; }
      if (!bubble) {
        bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        a.appendChild(bubble);
      }
      var shown = count < 100 ? '<span aria-hidden="true">' + count + '</span>' : '';
      bubble.innerHTML = shown + '<span class="visually-hidden">' + count + (count === 1 ? ' item' : ' items') + '</span>';
    });
  }

  /* ---------- open / close ---------- */
  function open() {
    lastFocus = document.activeElement;
    overlay.classList.add('on');
    drawer.classList.add('on');
    drawer.setAttribute('aria-hidden', 'false');
    var closeBtn = drawer.querySelector('[data-dj-close]');
    if (closeBtn) closeBtn.focus();
  }
  function close() {
    overlay.classList.remove('on');
    drawer.classList.remove('on');
    drawer.setAttribute('aria-hidden', 'true');
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }
  overlay.addEventListener('click', close);
  drawer.addEventListener('click', function (e) { if (e.target.closest('[data-dj-close]')) close(); });
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });

  /* ---------- render ---------- */
  function nextTierIndex(sub) {
    for (var i = 0; i < TIERS.length; i++) { if (sub < TIERS[i].threshold) return i; }
    return -1;
  }

  /* ---------- the gauge ladder (owner picks 2026-07-10) ----------
     E5 "the reading" from the gauge concepts, mirrored from
     mockups/cart-drawer.html: stations EVENLY spaced regardless of
     dollar value, labels anchored under their own dots, the subtotal
     travelling as a mercury reading above the fill. The ladder DOM is
     PERSISTENT - mounted once into [data-dj-ladder-mount] and updated
     in place - so the CSS transitions (fill width, reading position,
     station colour) and the crown celebration actually run between
     renders; rebuilding innerHTML every render would hard-cut them.

     STATIONS is recomputed rather than fixed, because a sold-out tier
     can be dropped at start-up or mid-session and the remaining
     stations must respace themselves. */
  var STATIONS = [];
  function computeStations() {
    STATIONS = TIERS.map(function (_t, i) { return (i + 0.5) / TIERS.length * 100; });
  }
  computeStations();
  var firstPaint = true;

  function ladderPos(sub) {
    if (!TIERS.length) return 0;
    if (sub <= TIERS[0].threshold) return (sub / TIERS[0].threshold) * STATIONS[0];
    for (var i = 0; i < TIERS.length - 1; i++) {
      if (sub <= TIERS[i + 1].threshold) {
        var f = (sub - TIERS[i].threshold) / (TIERS[i + 1].threshold - TIERS[i].threshold);
        return STATIONS[i] + f * (STATIONS[i + 1] - STATIONS[i]);
      }
    }
    var last = TIERS.length - 1;
    return Math.min(STATIONS[last] + (sub - TIERS[last].threshold) / TIERS[last].threshold * 20, 97);
  }

  function ensureMounts() {
    var bodyEl = drawer.querySelector('[data-dj-body]');
    var lm = bodyEl.querySelector('[data-dj-ladder-mount]');
    var rm = bodyEl.querySelector('[data-dj-rest-mount]');
    if (!lm || !rm) {
      bodyEl.innerHTML = '<div data-dj-ladder-mount></div><div data-dj-rest-mount></div>';
      lm = bodyEl.querySelector('[data-dj-ladder-mount]');
      rm = bodyEl.querySelector('[data-dj-rest-mount]');
    }
    return { body: bodyEl, ladderMount: lm, restMount: rm };
  }

  function mountLadder(mount) {
    var stops = TIERS.map(function (t, i) {
      return '<span class="dj-stop" style="left: ' + STATIONS[i].toFixed(1) + '%;" data-t="' + t.threshold + '"></span>';
    }).join('');
    var labs = TIERS.map(function (t, i) {
      return '<span class="dj-lab" style="left: ' + STATIONS[i].toFixed(1) + '%;" data-t="' + t.threshold + '">' +
        '<span class="dj-amt">$' + Math.round(t.threshold / 100) + '</span><span class="dj-nm">' + t.label + '</span></span>';
    }).join('');
    mount.innerHTML = '<span class="dj-eyebrow">' + COPY.eyebrow + '</span>' +
      '<div class="dj-ladder"><div class="dj-track">' +
      '<div class="dj-fill" style="width: 0%;"></div>' + stops +
      '<span class="dj-mread" style="left: 0%;"><span class="dj-mlabel">$0.00</span><span class="dj-riser"></span><span class="dj-mdot"></span></span>' +
      labs + '</div></div>' +
      '<p class="dj-progress" data-dj-progress></p>';
  }

  // The crown (owner pick): three eucalyptus droplets leap up together
  // off a freshly won station and fall back with gravity.
  function crownOnce(stop) {
    var old = stop.querySelector('.dj-splash');
    if (old) old.remove();
    var s = document.createElement('span');
    s.className = 'dj-splash';
    [-9, 0, 9].forEach(function (kx) {
      var i = document.createElement('i');
      i.style.setProperty('--kx', kx + 'px');
      s.appendChild(i);
    });
    stop.appendChild(s);
    setTimeout(function () { s.remove(); }, 1500);
  }

  // silent: state applied with transitions off and no celebration - the
  // initial paint of a pre-existing cart is a fact, not an event.
  function updateLadder(mount, sub, nextIdx, silent) {
    var root = mount.querySelector('.dj-ladder');
    if (silent) root.classList.add('dj-noanim');
    var p = ladderPos(sub).toFixed(1) + '%';
    root.querySelector('.dj-fill').style.width = p;
    var mread = root.querySelector('.dj-mread');
    mread.style.left = p;
    var mlabel = mread.querySelector('.dj-mlabel');
    if (silent) {
      mlabel.textContent = money(sub);
    } else if (mlabel.textContent !== money(sub)) {
      // crossfade to the new figure at journey's end, not mid-travel
      mlabel.classList.add('swap');
      setTimeout(function () { mlabel.textContent = money(sub); mlabel.classList.remove('swap'); }, 650);
    }
    root.querySelectorAll('[data-t]').forEach(function (el) {
      var wins = sub >= +el.getAttribute('data-t');
      var isNew = wins && !el.classList.contains('won');
      el.classList.toggle('won', wins);
      if (!silent && isNew && el.classList.contains('dj-stop')) {
        el.classList.remove('ripple');
        void el.offsetWidth; // restart the bloom
        el.classList.add('ripple');
        crownOnce(el);
        setTimeout(function () { el.classList.remove('ripple'); }, 2100);
      }
    });
    // 14 Sept 2026: gift step-down. Never say every gift is in the box while ANY
    // earned tier has been declined, even when a lower gift stepped in: one gift
    // is not every gift. The Add it back lines carry that state.
    var progress = nextIdx === -1 ? (declinedEarnedTiers(sub).length ? '' : COPY.allUnlocked)
      : COPY.progress
          .replace('[amount]', '<b>' + money(TIERS[nextIdx].threshold - sub) + '</b>')
          // 15 Sept 2026: cart ladder text row. The label used to be run through
          // lowerFirst(), which turned "Bath Tonic, and For You escape" into "bath
          // Tonic, ..." mid-sentence. Tier labels are proper names set in the theme
          // editor and print exactly as written, in the drawer and on the cart page
          // alike (this is the one builder both use). lowerFirst() had no other
          // caller and is gone. The copy pattern is otherwise unchanged.
          .replace('[gift]', TIERS[nextIdx].label);
    mount.querySelector('[data-dj-progress]').innerHTML = progress;
    if (silent) { void root.offsetWidth; root.classList.remove('dj-noanim'); }
  }

  /* The drawn parcel: hairline line-art in the roundel-motif register,
     so the shipping tier holds figures without a photograph (V4). */
  var PARCEL_SVG = '<svg viewBox="0 0 64 64" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M10 22 L32 12 L54 22 L54 44 L32 54 L10 44 Z" />' +
    '<path d="M10 22 L32 32 L54 22" /><path d="M32 32 L32 54" />' +
    '<path d="M21 17 L43 27" />' +
    '</svg>';

  /* V4 (owner pick 2026-07-12): below the spotlight sit ONLY the tiers
     still ahead - the earned live in Your gifts alone, so nothing is
     said twice. All earned -> no rows (the spotlight's close card
     holds the ladder's end). */
  function tierRowsHTML(sub, nextIdx) {
    var spot = spotlightTier(sub);
    var spotIdx = spot ? TIERS.indexOf(spot) : -1;
    return TIERS.map(function (t, idx) {
      if (spotIdx === -1 || idx <= spotIdx) return '';
      var isNext = idx === nextIdx;
      var cls = isNext ? 'next' : 'locked';
      var state = isNext ? 'Add ' + money(t.threshold - sub) : '';
      var fig;
      // 14 Sept 2026: gift images. The gift product's photo in a square slot
      // (56px, 64px from 720px); the drawn parcel sits centred in the same
      // slot where there is no product.
      var src = t.giftImgSm || t.giftImg || t.img;
      if (!src) { fig = '<div class="dj-fig dj-fig--drawn dj-fig--sq" aria-hidden="true">' + PARCEL_SVG + '</div>'; }
      else if ((t.count || 1) > 1 && t.img) {
        var imgs = '';
        for (var i = 0; i < (t.count || 1); i++) { imgs += '<img loading="lazy" src="' + esc(t.img) + '" alt="" />'; }
        fig = '<div class="dj-fig multi">' + imgs + '</div>';
      }
      else { fig = '<div class="dj-fig dj-fig--sq"><img loading="lazy" width="64" height="64" src="' + esc(src) + '" alt="' + esc(t.label) + '" /></div>'; }
      return '<div class="dj-tier ' + cls + '">' + fig +
        '<div><span class="dj-tier-amt">$' + Math.round(t.threshold / 100) + '</span><span class="dj-tier-label">' + t.label + '</span>' +
        '<span class="dj-tier-note">' + t.note + (t.value ? ' Worth ' + t.value + '.' : '') + '</span></div>' +
        '<span class="dj-state">' + state + '</span></div>';
    }).join('');
  }

  /* V4: the spotlight follows the LITERAL next tier (shipping holds it
     with the drawn parcel), so the card and the progress line always
     agree. Null when everything is earned. */
  function spotlightTier(sub) {
    for (var i = 0; i < TIERS.length; i++) {
      if (sub < TIERS[i].threshold) return TIERS[i];
    }
    return null;
  }

  function spotlightHTML(sub) {
    var t = spotlightTier(sub);
    if (!t) {
      if (declinedEarnedTiers(sub).length) return ''; // 14 Sept 2026: gift step-down
      /* all earned: the close hands back to the bath (owner-picked
         copy 2026-07-12), one card, no congratulation theatre */
      return '<div class="dj-next-gift dj-next-gift--done"><div class="dj-ng-body">' +
        '<span class="dj-ng-eyebrow">' + esc(COPY.doneEyebrow || 'Every gift is in the box') + '</span>' +
        '<h3>' + esc(COPY.doneLine || "From here, it's just you, the water, and letting go.") + '</h3>' +
        (COPY.doneNote ? '<p class="dj-ng-note">' + esc(COPY.doneNote) + '</p>' : '') +
      '</div></div>';
    }
    var away = (COPY.spotAway || '[amount] away').replace('[amount]', money(t.threshold - sub));
    var worth = t.value
      ? '<p class="dj-ng-worth">' + (COPY.spotWorth || 'Worth [value], with our compliments at [threshold].')
          .replace('[value]', '<b>' + esc(t.value) + '</b>')
          .replace('[threshold]', '$' + Math.round(t.threshold / 100)) + '</p>'
      : '';
    var fig;
    // 14 Sept 2026: spotlight image. Same chain as the rows: the gift
    // product's photo first, then the tier setting, then the drawn parcel.
    var spotSrc = t.giftImgLg || t.imgLarge || t.img;
    if (spotSrc) {
      var count = (t.count || 1) > 1 ? '<span class="dj-ng-count" aria-hidden="true">&times;' + t.count + '</span>' : '';
      fig = '<span class="dj-ng-fig"><img loading="lazy" width="128" height="128" src="' + esc(spotSrc) + '"' +
        (t.giftImgLg && t.giftImgLg2x ? ' srcset="' + esc(t.giftImgLg) + ' 256w, ' + esc(t.giftImgLg2x) + ' 512w" sizes="128px"' : '') +
        ' alt="' + esc(t.label) + '" />' + count + '</span>';
    } else {
      fig = '<span class="dj-ng-fig"><span class="dj-ng-drawn" aria-hidden="true">' + PARCEL_SVG + '</span></span>';
    }
    return '<div class="dj-next-gift">' + fig +
      '<div class="dj-ng-body">' +
        '<span class="dj-ng-eyebrow">' + away + '</span>' +
        '<h3>' + esc(t.label) + '</h3>' +
        '<p class="dj-ng-note">' + esc(t.note) + '</p>' + worth +
      '</div></div>';
  }

  /* Your gifts (V4): everything earned, grouped under its own eyebrow
     with the combined worth stated. Tier config drives the figures;
     a lower gift tier is superseded by a higher one to match the
     reconciler (only the highest earned gift is held). Shipping joins
     once earned, carrying the parcel. Worth strings are display copy
     ("$24.95"), parsed only for the sum. */
  function earnedTiers(sub) {
    var earned = TIERS.filter(function (t) { return sub >= t.threshold; });
    var withGift = earned.filter(function (t) { return t.giftVariantId; });
    if (withGift.length > 1) {
      // 14 Sept 2026: gift step-down. Keep the tier the reconciler actually
      // grants (activeGiftTier), not simply the highest earned one, so a
      // stepped-down gift shows as the gift in the box.
      var keepIdx = activeGiftTier(sub);
      var keep = keepIdx === null ? null : TIERS[keepIdx];
      earned = earned.filter(function (t) { return !t.giftVariantId || t === keep; });
    }
    return earned;
  }

  function giftsHTML(sub) {
    var earned = earnedTiers(sub);
    // 14 Sept 2026: gift step-down. Every declined earned tier leaves the rows
    // and the worth sum; in its place sits one line with the way back. More
    // than one can be out at once now (decline $259, take the $149, decline
    // that too), so the same markup is drawn once per declined earned tier.
    var outs = declinedEarnedTiers(sub);
    if (outs.length) earned = earned.filter(function (t) { return outs.indexOf(t) === -1; });
    var outLine = outs.map(function (out) {
      return '<div class="dj-left-out">' + esc(out.label) + ' left out. <button type="button" class="dj-undecline" data-dj-undecline="' + out.giftVariantId + '">Add it back</button></div>';
    }).join('');
    if (!earned.length) return outs.length ? '<div class="dj-gifts"><div class="dj-lines">' + outLine + '</div></div>' : '';
    var total = 0;
    earned.forEach(function (t) {
      var v = parseFloat(String(t.value || '').replace(/[^0-9.]/g, ''));
      if (!isNaN(v)) total += v;
    });
    var rows = earned.map(function (t) {
      var fig;
      // 14 Sept 2026: gift images. The gift product's own photo in a square
      // slot (72px, 88px from 720px) with the label as alt text; the
      // free-shipping row keeps the parcel centred in the same slot so the
      // column reads as one system. 176w serves 2x at 88px, 128w the phone.
      var src = t.giftImg || t.img;
      if (!src) {
        fig = '<span class="dj-gift-sq dj-fig--drawn" aria-hidden="true">' + PARCEL_SVG + '</span>';
      } else if ((t.count || 1) > 1 && t.img) {
        var fanImgs = '';
        for (var i = 0; i < t.count; i++) { fanImgs += '<img src="' + esc(t.img) + '" alt="" />'; }
        fig = '<span class="dj-gift-fan" aria-hidden="true">' + fanImgs + '</span>';
      } else {
        fig = '<span class="dj-gift-sq"><img loading="lazy" width="88" height="88" src="' + esc(src) + '"' +
          (t.giftImg && t.giftImgSm ? ' srcset="' + esc(t.giftImgSm) + ' 128w, ' + esc(t.giftImg) + ' 176w" sizes="(min-width: 720px) 88px, 72px"' : '') +
          ' alt="' + esc(t.label) + '" /></span>';
      }
      return '<div class="dj-line dj-line--gift">' + fig +
        // 14 Sept 2026: gift note. The tier's note says what the gift is; the
        // old line stands in only when a tier has no note.
        '<div><h3>' + esc(t.label) + '</h3><p class="dj-meta">' + esc(t.note || 'With our compliments') + '</p></div>' +
        '<div>' + (t.value ? '<span class="dj-gift-was">' + esc(t.value) + '</span>' : '') +
        '<p class="dj-pr">' + money(0) + '</p><span class="dj-gift-inbox">In the box</span>' +
        // 14 Sept 2026: gift opt-out. Only real gift lines can be declined; the shipping tier has no product.
        (t.giftVariantId ? '<button type="button" class="dj-decline" data-dj-decline="' + t.giftVariantId + '">No thanks</button>' : '') +
        '</div>' +
      '</div>';
    }).join('');
    var worthLine = total > 0 ? '<span class="dj-g-worth">Worth <span class="dj-num">' + money(total * 100) + '</span></span>' : '';
    return '<div class="dj-gifts">' + worthLine +
      '<span class="dj-g-eyebrow">' + esc(COPY.giftsEyebrow || 'Your gifts') + '</span>' +
      '<div class="dj-lines">' + rows + outLine + '</div></div>';
  }

  /* ---------- 14 Sept 2026: line discount ----------
     A discount used to be invisible in the side cart: the customer saw the
     subtotal drop and nothing saying why. Earlier tonight that was answered with
     a summary row in the drawer foot and a matching slot above the cart page
     Subtotal. Both are gone. At the bottom of a tall cart the row got lost, and
     it never said WHICH product earned the saving. The discount now sits on the
     line it belongs to, which is also exactly what the cart page has always done
     natively in sections/main-cart-items.liquid, so the two surfaces agree.

     Everything needed is already in the /cart.js response this script fetches,
     so nothing new is requested: original_price / final_price for the unit pair,
     original_line_price / final_line_price for the line total, and
     line_level_discount_allocations for the titles. items[].discounts is the
     older shape of the same line-level data and is read ONLY when
     line_level_discount_allocations is absent, never both.

     Gift lines never reach here: the renderer returns '' for them well before
     these are called. They are $0, so they cannot carry a real saving, and a
     percentage discount allocating 0 to one of them must never print a tag.
     giftSubtotal() and the ladder maths are untouched; this only reads the cart. */

  /* The cart page's tag icon, snippets/icon-discount.liquid, inlined so the two
     surfaces carry the same mark. currentColor, so it takes the row's ink. */
  var LD_TAG_SVG = '<svg aria-hidden="true" focusable="false" viewBox="0 0 12 12">' +
    '<path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7' +
    'l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z"' +
    ' fill="currentColor"></path></svg>';

  /* The discount titles allocated to one line, in Shopify's own order, blanks
     dropped and repeats collapsed. */
  function lineDiscountTitles(item) {
    var seen = {};
    var out = [];
    function add(title) {
      var t = String(title == null ? '' : title).trim();
      if (!t || seen[t]) return;
      seen[t] = 1;
      out.push(t);
    }
    var allocs = item.line_level_discount_allocations;
    if (allocs && allocs.length) {
      allocs.forEach(function (a) {
        add(a.discount_application && a.discount_application.title);
      });
      return out;
    }
    (item.discounts || []).forEach(function (d) { add(d.title); });
    return out;
  }

  /* Under the product name: the struck original unit price with the discounted
     one beside it, then the title on its own line behind the tag icon. Returns
     '' when the line is not discounted, so no empty chrome appears. */
  function lineDiscountHTML(item) {
    var out = '';
    if (Number(item.original_price) > Number(item.final_price)) {
      out += '<p class="dj-ld-unit"><s>' + money(item.original_price) + '</s>' +
        '<span class="dj-ld-now">' + money(item.final_price) + '</span></p>';
    }
    var titles = lineDiscountTitles(item);
    if (titles.length) {
      out += '<ul class="dj-ld-tags">' + titles.map(function (t) {
        return '<li class="dj-ld-tag">' + LD_TAG_SVG + '<span>' + esc(t) + '</span></li>';
      }).join('') + '</ul>';
    }
    return out;
  }

  /* The line total on the right, struck original stacked above the discounted
     figure when the two differ. It costs no width: the column is already as wide
     as REMOVE below it, and the struck figure is smaller than the price itself,
     so the title column is not squeezed at 375px. Unchanged output when the line
     carries no discount. */
  function linePriceHTML(item) {
    if (Number(item.original_line_price) > Number(item.final_line_price)) {
      return '<s class="dj-ld-old">' + money(item.original_line_price) + '</s>' +
        money(item.final_line_price);
    }
    return money(item.final_line_price);
  }

  /* ---------- 14 Sept 2026: cart page ladder ----------
     The full /cart page lost its gift bar when the old engine was switched off
     there. This script already runs on every page and already holds the tier
     state, so the cart page gets a mount point and the SAME ladder is drawn into
     it: mountLadder and updateLadder, untouched, so the bar, the travelling
     subtotal and the "spend another $X for [gift]" line are the drawer's, not a
     copy. 14 Sept 2026: that mount moved to the TOP of the cart page (Suzy's
     call). It is now sections/cart-ladder-39d.liquid, a section of its own placed
     first in templates/cart.json, which also puts it outside <cart-items>, whose
     entire innerHTML assets/cart.js rewrites on every quantity change.

     Two deliberate limits for tonight. There is no "No thanks" here: declining
     stays a drawer action, so /cart adds no new control. And the earned gift
     names come from earnedTiers() with declinedEarnedTiers() removed, so a gift
     the customer said no to is never listed as being in the box; updateLadder
     already suppresses the "every gift" line on its own when a tier is declined.
     The subtotal is giftSubtotal(), the same after-automatic-discounts figure the
     ladder has always used. */
  var cartLadderFirstPaint = true;
  function renderCartLadder() {
    var host = document.querySelector('[data-dj-cart-ladder]');
    if (!host) return; // every page but /cart
    if (!cart || !TIERS.length || !cart.items.length) { host.hidden = true; return; }
    host.hidden = false;

    var sub = giftSubtotal(cart);
    var mount = host.querySelector('[data-dj-cart-ladder-mount]');
    var freshMount = !mount.querySelector('.dj-ladder');
    if (freshMount) mountLadder(mount);
    // Silent on the first paint for the same reason as the drawer: a cart that
    // was already earning its gift when the page loaded is a fact, not an event.
    updateLadder(mount, sub, nextTierIndex(sub), freshMount && cartLadderFirstPaint);
    cartLadderFirstPaint = false;

    var outs = declinedEarnedTiers(sub);
    var earned = earnedTiers(sub).filter(function (t) { return outs.indexOf(t) === -1; });
    var namesEl = host.querySelector('[data-dj-cart-gifts]');
    if (namesEl) {
      namesEl.innerHTML = earned.length
        ? '<span class="dj-g-eyebrow">' + esc(COPY.giftsEyebrow || 'Your gifts') + '</span>' +
          earned.map(function (t) { return esc(t.label); }).join(', ')
        : '';
    }
  }

  /* Goes with the bath (V4): the curated cross-sell rows, hiding
     anything already in the cart; '' when nothing is left or nothing
     is configured. */
  function recosHTML() {
    var hasGift = cart.items.some(isGiftLine);
    var shown = RECOS.filter(function (r) {
      if (!r.variantId) return false;
      if (cart.items.some(function (l) { return l.id === r.variantId; })) return false;
      if (hasGift && /mask/i.test(r.title || '')) return false;
      return true;
    });
    if (!shown.length) return '';
    var rows = shown.map(function (r) {
      return '<div class="dj-es-row">' +
        (r.img ? '<a href="' + esc(r.url) + '"><img loading="lazy" src="' + esc(r.img) + '" alt="" /></a>' : '<span class="dj-thumb-empty" aria-hidden="true"></span>') +
        '<div><h3><a href="' + esc(r.url) + '">' + esc(r.title) + '</a></h3>' +
        '<p class="dj-es-meta">' + esc(r.meta) + '</p>' +
        '<p class="dj-es-pr">' + money(r.price) + '</p></div>' +
        '<button class="dj-es-add" data-dj-offer="' + r.variantId + '">Add</button></div>';
    }).join('');
    return '<div class="dj-reco"><span class="dj-r-eyebrow">' + esc(COPY.recoEyebrow || 'Goes with the bath') + '</span>' + rows + '</div>';
  }

  /* The empty cart sells (EE 8.3, owner pick 2026-07-11): the empty
     line, the configured best sellers as add-able rows, the quiz link.
     Falls back to the plain empty line when no suggestions are set. */
  function emptyHTML() {
    if (!BEST_SELLERS.length) return '<p class="dj-empty">' + COPY.empty + '</p>';
    var rows = BEST_SELLERS.map(function (b) {
      return '<div class="dj-es-row">' +
        (b.img ? '<a href="' + esc(b.url) + '"><img loading="lazy" src="' + esc(b.img) + '" alt="" /></a>' : '<span class="dj-thumb-empty" aria-hidden="true"></span>') +
        '<div><h3><a href="' + esc(b.url) + '">' + esc(b.title) + '</a></h3>' +
        '<p class="dj-es-meta">' + esc(b.meta) + '</p>' +
        '<p class="dj-es-pr">' + money(b.price) + '</p></div>' +
        '<button class="dj-es-add" data-dj-offer="' + b.variantId + '">Add</button></div>';
    }).join('');
    var quiz = COPY.emptyQuizUrl
      ? '<span class="dj-e-quiz">' + esc(COPY.emptyQuiz || '') + ' <a href="' + esc(COPY.emptyQuizUrl) + '">' + esc(COPY.emptyQuizLabel || '') + '</a></span>'
      : '';
    return '<div class="dj-e-head"><p class="dj-e-line">' + esc(COPY.empty) + '</p>' +
      (COPY.emptySub ? '<p class="dj-e-sub">' + esc(COPY.emptySub) + '</p>' : '') + '</div>' +
      '<div class="dj-e-suggest"><span class="dj-es-eyebrow">' + esc(COPY.emptyEyebrow || '') + '</span>' + rows + '</div>' + quiz;
  }

  function render() {
    if (!cart) return;
    // Ladder maths must match the reconciler's earning rule, or the
    // rail could show a tier as won that the reconciler won't grant.
    var sub = giftSubtotal(cart);
    var nextIdx = nextTierIndex(sub);

    var countEl = drawer.querySelector('[data-dj-count]');
    if (countEl) countEl.textContent = '(' + paidCount(cart) + ')';

    var m = ensureMounts();
    // Clear any adding overlay prepended over existing contents; the
    // empty-cart add path replaced the body wholesale, which
    // ensureMounts() has already rebuilt.
    m.body.querySelectorAll('.dj-adding').forEach(function (n) { n.remove(); });

    // An empty cart shows the empty line and a way in, not a gift ladder
    // and a live Checkout link with nothing to check out.
    if (TIERS.length > 0 && cart.items.length > 0) {
      var freshMount = !m.ladderMount.querySelector('.dj-ladder');
      if (freshMount) mountLadder(m.ladderMount);
      if (firstPaint) {
        updateLadder(m.ladderMount, sub, nextIdx, true);
      } else {
        // A fresh mount sits at $0.00: one forced frame first, so the
        // first add pours in (fill + reading travel from the origin).
        if (freshMount) void m.ladderMount.offsetWidth;
        updateLadder(m.ladderMount, sub, nextIdx, false);
      }
    } else {
      m.ladderMount.innerHTML = '';
    }
    firstPaint = false;

    var lines = cart.items.length === 0 ? emptyHTML()
      : cart.items.map(function (item, idx) {
          var line = idx + 1;
          var meta = item.variant_title && item.variant_title.indexOf('Default') === -1 ? item.variant_title : '';
          if (isGiftLine(item)) {
            // V4: the earned gifts render in the Your gifts group
            // alone. The real $0 line stays in the cart (the
            // reconciler owns it); it is just not repeated here.
            return '';
          }
          return '<div class="dj-line">' +
            (item.image ? '<img src="' + esc(thumb(item.image, 144)) + '" alt="" />' : '<span class="dj-thumb-empty" aria-hidden="true"></span>') +
            '<div><h3><a href="' + esc(item.url) + '">' + esc(SHORT[item.handle] || item.product_title) + '</a></h3>' +
            (meta ? '<p class="dj-meta">' + esc(meta) + '</p>' : '') +
            lineDiscountHTML(item) + // 14 Sept 2026: line discount
            '<span class="dj-qty"><button data-dj-dec="' + line + '" aria-label="Decrease quantity">&minus;</button> ' + item.quantity +
            ' <button data-dj-inc="' + line + '" data-dj-qty="' + item.quantity + '" aria-label="Increase quantity">+</button></span></div>' +
            '<div><p class="dj-pr">' + linePriceHTML(item) + '</p>' + // 14 Sept 2026: line discount
            '<button class="dj-remove" data-dj-remove="' + line + '">Remove</button></div></div>';
        }).join('');

    var offers = OFFERS.filter(function (o) {
      return o.variantId &&
        cart.items.some(function (l) { return l.handle === o.trigger; }) &&
        !cart.items.some(function (l) { return l.id === o.variantId; });
    }).map(function (o) {
      return '<div class="dj-offer">' +
        (o.img ? '<img loading="lazy" src="' + esc(o.img) + '" alt="" />' : '') +
        '<div>' + (o.title ? '<h3>' + esc(o.title) + '</h3>' : '') + '<p class="dj-offer-line">' + o.line + '</p>' +
        '<p class="dj-offer-price">' + (o.was ? '<span class="dj-offer-was">' + money(o.was) + '</span>' : '') + money(o.price) + '</p>' +
        '<button class="dj-offer-add" data-dj-offer="' + o.variantId + '">Add &middot; ' + money(o.price) + '</button></div></div>';
    }).join('');

    /* V4 order (owner picks 2026-07-12): gauge, purchases, Goes with
       the bath (actions beside what prompted them), Your gifts, the
       literal-next spotlight (or its close), only the tiers still
       ahead, then any conditional offer. Everything below the ladder
       rebuilds wholesale each render; only the ladder animates in
       place. */
    var hasItems = cart.items.length > 0;
    var recos = hasItems ? recosHTML() : '';
    var gifts = (TIERS.length > 0 && hasItems) ? giftsHTML(sub) : '';
    var spotlight = (TIERS.length > 0 && hasItems) ? spotlightHTML(sub) : '';
    var tierRows = (TIERS.length > 0 && hasItems) ? tierRowsHTML(sub, nextIdx) : '';
    var tiers = tierRows ? '<div class="dj-tiers">' + tierRows + '</div>' : '';
    m.restMount.innerHTML = '<div class="dj-lines">' + lines + '</div>' + recos + gifts + spotlight + tiers + offers;
    m.body.classList.remove('dj-busy');

    /* The express slot (wallet buttons) sits STATIC between the two
       foot mounts, directly below Checkout: these iframes do not
       survive innerHTML rewrites, so we only toggle visibility. Hidden
       at zero: an express button over an empty cart is a broken
       promise. Everything after it (View the full cart, the pay line)
       renders into the second mount. */
    var express = drawer.querySelector('[data-dj-express]');
    if (express) express.hidden = cart.items.length === 0;
    var footAfter = drawer.querySelector('[data-dj-foot-after]');

    if (cart.items.length === 0) {
      drawer.querySelector('[data-dj-foot]').innerHTML =
        '<a class="dj-checkout dj-checkout--ghost" href="' + esc(COPY.emptyShopUrl || '/collections/all-tonics') + '">' + esc(COPY.emptyShopLabel || 'Shop the Bath Tonics') + '</a>';
      if (footAfter) footAfter.innerHTML = ''; // 15 Sept 2026: foot tighten. Pay line no longer rendered.
    } else {
      /* 15 Sept 2026: foot tighten (Suzy). The foot was too tall, most of all
         on a phone, so two of its lines are no longer rendered at all rather
         than hidden: the shipping facts line (.dj-ship: "Standard $9.95, free
         over $129 ..." / "Shipping is free ...") that sat between the subtotal
         and Checkout, and the payment methods line (.dj-pay: "Visa, Mastercard
         ...") that sat under View cart in the second mount. The section
         settings behind them (ship_facts_line, ship_gap_line, ship_done_line,
         pay_line) still exist and still land in COPY; they are simply not
         read here, so nothing breaks if they are edited or blanked. The $129
         free-shipping GIFT TIER is a different thing entirely: it is a tier
         in TIERS, drawn by the ladder and the gifts rows, and is untouched.
         The Subtotal line, the Checkout button and View cart are exactly as
         they were; only the spacing around them changed, in the section CSS. */
      drawer.querySelector('[data-dj-foot]').innerHTML =
        '<div class="dj-sub"><span>Subtotal</span><span class="dj-sub-pr">' + money(cart.total_price) + '</span></div>' +
        /* 14 Sept 2026: guarantee line off (not rendered, whatever the setting holds)
        (COPY.guarantee ? '<p class="dj-guarantee">' + esc(COPY.guarantee) + '</p>' : '') +
        */
        '<a class="dj-checkout" href="/checkout">' + COPY.checkout + '</a>' +
        // 14 Sept 2026: view cart link. Rendered into the SAME foot mount as
        // Checkout, on the line under it: the button is full width, so a link
        // sharing its row would have to narrow the primary action. It carries
        // the section's existing .dj-viewcart rule unchanged (12px Nunito Sans,
        // #737373, underlined, black on hover), which is already the foot's
        // subordinate register, so no new CSS is introduced. This replaces the
        // older link that sat in the mount below the wallet slot, so the drawer
        // still holds exactly one way through to the cart page.
        '<a class="dj-viewcart" href="' + esc(CFG.cartUrl) + '">View cart</a>';
      if (footAfter) footAfter.innerHTML = ''; // 15 Sept 2026: foot tighten. Pay line no longer rendered.
    }

    // 14 Sept 2026: cart page ladder. Drawn from the same pass that draws the
    // drawer, so the two can never disagree. Both are no-ops off the cart page.
    renderCartLadder();
  }

  /* ---------- drawer interactions (delegated) ---------- */
  drawer.addEventListener('click', function (e) {
    var t = e.target;
    var inc = t.closest('[data-dj-inc]');
    var dec = t.closest('[data-dj-dec]');
    var rem = t.closest('[data-dj-remove]');
    var off = t.closest('[data-dj-offer]');
    var no = t.closest('[data-dj-decline]');       // 14 Sept 2026: gift opt-out
    var back = t.closest('[data-dj-undecline]');   // 14 Sept 2026: gift opt-out
    if (no) { setDeclined(no.dataset.djDecline, true).catch(function () {}); return; }
    if (back) { setDeclined(back.dataset.djUndecline, false).catch(function () {}); return; }
    if (inc) { change(+inc.dataset.djInc, +inc.dataset.djQty + 1); }
    else if (dec) {
      var line = +dec.dataset.djDec;
      var item = cart.items[line - 1];
      if (item) change(line, item.quantity - 1);
    }
    else if (rem) { change(+rem.dataset.djRemove, 0); }
    else if (off) { addId(+off.dataset.djOffer).catch(function () {}); }
  });

  document.addEventListener('click', function (e) {
    var pageAdd = e.target.closest('[data-dj-page-add]');
    if (!pageAdd) return;
    e.preventDefault();
    open();
    addId(+pageAdd.dataset.djPageAdd).catch(function () {});
  });

  /* ---------- checkout waits for the cart to settle ----------
     The drawer's Checkout is a link and the cart page's is a submit;
     both would otherwise leave mid-write. The ghost link on an empty
     cart is not checkout at all (it goes shopping), so it is left
     alone. */
  document.addEventListener('click', function (e) {
    // 14 Sept 2026: view cart link. The View cart link joins this same handler
    // rather than getting one of its own: /cart is server-rendered, so it must
    // also wait on settleThen (and its 2.5s cap) or it can paint a gift line the
    // reconciler is still writing. settleThen sets `leaving`, which also keeps
    // the arrival from reloading itself.
    var link = e.target.closest('.dj-checkout, .dj-viewcart');
    if (!link || link.classList.contains('dj-checkout--ghost')) return;
    if (link.dataset.djGoing) return; // our own second click, let it through
    e.preventDefault();
    link.dataset.djGoing = '1';
    link.classList.add('dj-going');
    settleThen(function () { window.location.href = link.getAttribute('href') || '/checkout'; });
  });

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('button[name="checkout"]');
    if (!btn || btn.dataset.djGoing) return;
    var form = btn.form || btn.closest('form');
    if (!form) return;
    e.preventDefault();
    btn.dataset.djGoing = '1';
    settleThen(function () {
      // requestSubmit keeps the button as the submitter, so the cart
      // form still posts "checkout" rather than a plain update. The
      // fallback plants the same field by hand for older browsers.
      if (form.requestSubmit) {
        form.requestSubmit(btn);
      } else {
        var field = document.createElement('input');
        field.type = 'hidden';
        field.name = 'checkout';
        field.value = '';
        form.appendChild(field);
        form.submit();
      }
    });
  });

  /* ---------- intercept every add-to-cart form ---------- */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    var action = (e.submitter && e.submitter.getAttribute('formaction')) || form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') === -1) return;

    var fd;
    try { fd = new FormData(form, e.submitter); } catch (err) { fd = new FormData(form); }
    // Upsell idiom: a submit button named "add" carrying the variant id.
    if (!fd.get('id') && e.submitter && e.submitter.name === 'add' && e.submitter.value) {
      fd.set('id', e.submitter.value);
    }
    if (!fd.get('id')) return; // nothing addable: let the form behave normally

    // Dawn port: already claimed (Dawn's product-form, or any other
    // script that called preventDefault before us) means the add is
    // someone else's. Acknowledge, never fetch: one add, not two.
    var claimed = e.defaultPrevented;
    if (!claimed) e.preventDefault();
    fd.delete('add');

    // Open NOW, before any network: the drawer showing the pouring mark
    // over the known contents is the acknowledgement; the add and cart
    // fetch catch up and render() replaces it. The mark lives in a
    // <template data-dj-loader> in the section (the logo pouring into
    // its own bowl); under prefers-reduced-motion the CSS hides the
    // mark and the static text note carries the state alone.
    var body = drawer.querySelector('[data-dj-body]');
    body.classList.add('dj-busy');
    var tpl = drawer.querySelector('[data-dj-loader]');
    var addingHTML = tpl ? tpl.innerHTML : '<p class="dj-adding-note">Adding to the cart</p>';
    if (!cart || cart.items.length === 0) {
      body.innerHTML = addingHTML;
    } else {
      body.insertAdjacentHTML('afterbegin', addingHTML);
    }
    open();

    if (claimed) {
      // The refresh arrives through renderContents() / cartUpdate. If
      // neither comes (a script we do not know), settle from /cart.js
      // anyway so the drawer never sits on the pouring mark.
      clearTimeout(claimedTimer);
      claimedTimer = setTimeout(function () {
        if (drawer.querySelector('.dj-adding')) refresh().catch(clearAdding);
      }, 2500);
      return;
    }

    fetch(CFG.addUrl, { method: 'POST', credentials: 'same-origin', body: fd })
      .then(
        function (r) {
          if (!r.ok) { close(); form.submit(); return; } // sold out etc: full-page fallback
          // The add succeeded: a refresh failure must NOT re-submit the
          // form (double add). Worst case the note is cleared and the
          // drawer shows the last known contents.
          return refresh().catch(clearAdding);
        },
        function () { close(); form.submit(); } // network down: full-page fallback
      );
  });

  var claimedTimer = null;
  function clearAdding() {
    var body = drawer.querySelector('[data-dj-body]');
    if (!body) return;
    body.classList.remove('dj-busy');
    body.querySelectorAll('.dj-adding, .dj-adding-note').forEach(function (n) { n.remove(); });
  }

  /* ---------- Dawn seams: the <cart-drawer> host and pubsub ----------
     See the header comment. product-form.js calls these three on
     whatever document.querySelector('cart-drawer') returns. */
  var host = document.querySelector('[data-dj-host]');
  if (host) {
    host.getSectionsToRender = function () { return []; };
    host.setActiveElement = function (el) { lastFocus = el || lastFocus; };
    host.renderContents = function () {
      clearTimeout(claimedTimer);
      if (!drawer.classList.contains('on')) open();
      refresh().catch(clearAdding);
    };
  }
  // 14 Sept 2026: GemPages add-to-cart hook (home page buttons never submit the form)
  window.addEventListener('gp:add-to-cart-success', function () {
    clearTimeout(claimedTimer);
    if (!drawer.classList.contains('on')) open();
    refresh().catch(clearAdding);
  });
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (evt) {
      if (onCartPage) {
        // 14 Sept 2026: cart page ladder. Dawn's cart.js owns the cart page's own
        // markup, the ladder is ours. Until 6 Oct 2026 a quantity change here only
        // redrew the ladder and skipped the gift reconciler, because a reconciler
        // write reloaded the page. The ladder then said one gift while the cart
        // still held another (seen by Suzy 6 Oct: stepping down from $432 left
        // the $432 gift in the cart until checkout). Now refresh() runs here too:
        // the reconciler swaps the gift and rerenderCartPage() redraws the lines
        // in place, no reload (see refresh()).
        if (!document.querySelector('[data-dj-cart-ladder]')) return;
        refresh().catch(function () {});
        return;
      }
      if (evt && evt.source === 'product-form') return; // renderContents covers this one
      refresh().catch(clearAdding);
    });
    subscribe(PUB_SUB_EVENTS.cartError, function () {
      clearTimeout(claimedTimer);
      clearAdding();
      close();
    });
  }

  /* ---------- the header cart opens the drawer (owner call 2026-07-11) ----------
     Scoped to the chrome's own links (nav Cart, the mobile mini cart,
     the mobile menu Cart); the href stays routes.cart_url as the no-JS
     fallback. On /cart itself the links navigate normally - the page
     IS the cart there (V2, the drawer at rest). */
  var onCartPage = location.pathname === CFG.cartUrl;
  if (!onCartPage) {
    document.querySelectorAll('#cart-icon-bubble, .header__icon--cart').forEach(function (a) {
      a.setAttribute('role', 'button');
      a.setAttribute('aria-haspopup', 'dialog');
    });
  }
  document.addEventListener('click', function (e) {
    if (onCartPage) return;
    var link = e.target.closest('#cart-icon-bubble, .header__icon--cart');
    if (!link) return;
    e.preventDefault();
    // 14 Sept 2026: refresh on icon open
    open(); refresh().catch(function () {});
  });

  /* ---------- initial load ----------
     Runs the gift reconciler on every page, which matters most on
     the server-rendered cart page: its quantity forms POST outside
     this script, so the earned tier is settled on the next load. */
  refresh().catch(function () {});
})();
